// @ts-nocheck
import { CreditReport, CreditReportOrder, CreditReportRecord } from '../types';

export class CreditReportService {
  private static instance: CreditReportService;
  private reports: CreditReport[] = [];
  private crRecords: CreditReportRecord[] = [];
  private listeners: Array<(reports: CreditReport[]) => void> = [];
  private crRecordListeners: Array<(records: CreditReportRecord[]) => void> = [];

  static getInstance(): CreditReportService {
    if (!CreditReportService.instance) {
      CreditReportService.instance = new CreditReportService();
    }
    return CreditReportService.instance;
  }

  constructor() {
    // Initialize with some mock reports
    this.reports = this.getMockReports();
    // Initialize with some mock PAW records
    this.crRecords = this.getMockCRRecords();
  }

  private getMockCRRecords(): CreditReportRecord[] {
    return [
      {
        id: 'PAW-001',
        clientId: 'client_001',
        clientName: 'John Smith',
        bureau: 'experian',
        pullDate: '2025-09-15',
        productCode: 'PAW',
        reportKey: 'paw-001-key',
        website: 'www.equifax.com',
        recordType: 'Credit Bureau',
        recordDate: '2025-09-15',
        status: 'Completed',
        overview: {
          minimumMonthlyPayment: 0,
          realEstate: 0,
          installment: 0,
          lineOfCredit: 0,
          totalDebts: 0,
          totalCreditors: 0,
          goodStanding: 0,
          openAccounts: 0,
          closedAccounts: 0,
          creditRepositoryIncluded: ['Equifax']
        },
        creditors: {
          auto: [],
          mortgages: [],
          creditCards: [],
          others: []
        },
        systemInfo: {
          createdBy: 'System',
          status: 'success',
          createdDate: '2025-09-15T10:30:00Z',
          totalFilesAttached: 0,
          attachedFiles: []
        },
        relatedReports: [],
        approvalHistory: []
      },
      {
        id: 'PAW-002',
        clientId: 'client_001',
        clientName: 'John Smith',
        bureau: 'transunion',
        pullDate: '2025-09-18',
        productCode: 'PAW',
        reportKey: 'paw-002-key',
        website: 'www.transunion.com',
        recordType: 'Credit Bureau',
        recordDate: '2025-09-18',
        status: 'In Progress',
        overview: {
          minimumMonthlyPayment: 0,
          realEstate: 0,
          installment: 0,
          lineOfCredit: 0,
          totalDebts: 0,
          totalCreditors: 0,
          goodStanding: 0,
          openAccounts: 0,
          closedAccounts: 0,
          creditRepositoryIncluded: ['TransUnion']
        },
        creditors: {
          auto: [],
          mortgages: [],
          creditCards: [],
          others: []
        },
        systemInfo: {
          createdBy: 'System',
          status: 'success',
          createdDate: '2025-09-18T14:20:00Z',
          totalFilesAttached: 0,
          attachedFiles: []
        },
        relatedReports: [],
        approvalHistory: []
      },
      {
        id: 'PAW-003',
        clientId: 'client_002',
        clientName: 'Amanda Johnson',
        bureau: 'experian',
        pullDate: '2025-09-20',
        productCode: 'PAW',
        reportKey: 'paw-003-key',
        website: 'www.experian.com',
        recordType: 'Credit Bureau',
        recordDate: '2025-09-20',
        status: 'Completed',
        overview: {
          minimumMonthlyPayment: 0,
          realEstate: 0,
          installment: 0,
          lineOfCredit: 0,
          totalDebts: 0,
          totalCreditors: 0,
          goodStanding: 0,
          openAccounts: 0,
          closedAccounts: 0,
          creditRepositoryIncluded: ['Experian']
        },
        creditors: {
          auto: [],
          mortgages: [],
          creditCards: [],
          others: []
        },
        systemInfo: {
          createdBy: 'System',
          status: 'success',
          createdDate: '2025-09-20T09:15:00Z',
          totalFilesAttached: 0,
          attachedFiles: []
        },
        relatedReports: [],
        approvalHistory: []
      },
      {
        id: 'PAW-004',
        clientId: 'client_002',
        clientName: 'Amanda Johnson',
        bureau: 'tri_merge',
        pullDate: '2025-09-22',
        productCode: 'PAW',
        reportKey: 'paw-004-key',
        website: 'www.annualcreditreport.com',
        recordType: 'Tri-Merge Report',
        recordDate: '2025-09-22',
        status: 'Completed',
        overview: {
          minimumMonthlyPayment: 0,
          realEstate: 0,
          installment: 0,
          lineOfCredit: 0,
          totalDebts: 0,
          totalCreditors: 0,
          goodStanding: 0,
          openAccounts: 0,
          closedAccounts: 0,
          creditRepositoryIncluded: ['Experian', 'Equifax', 'TransUnion']
        },
        creditors: {
          auto: [],
          mortgages: [],
          creditCards: [],
          others: []
        },
        systemInfo: {
          createdBy: 'System',
          status: 'success',
          createdDate: '2025-09-22T11:45:00Z',
          totalFilesAttached: 0,
          attachedFiles: []
        },
        relatedReports: [],
        approvalHistory: []
      }
    ];
  }

  // Subscribe to report updates
  subscribe(listener: (reports: CreditReport[]) => void): () => void {
    this.listeners.push(listener);
    return () => {
      this.listeners = this.listeners.filter(l => l !== listener);
    };
  }

  // Subscribe to CR record updates
  subscribeToCRRecords(listener: (records: CreditReportRecord[]) => void): () => void {
    this.crRecordListeners.push(listener);
    return () => {
      this.crRecordListeners = this.crRecordListeners.filter(l => l !== listener);
    };
  }

  private notifyListeners() {
    this.listeners.forEach(listener => listener([...this.reports]));
  }

  private notifyCRRecordListeners() {
    this.crRecordListeners.forEach(listener => listener([...this.crRecords]));
  }

  // Order a new credit report
  async orderCreditReport(order: CreditReportOrder): Promise<CreditReport> {
    const newReport: CreditReport = {
      id: `cr_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`,
      clientId: order.clientId,
      clientName: order.clientName,
      bureau: order.bureau,
      reportType: order.reportType,
      orderDate: new Date().toISOString(),
      status: 'pending',
      orderedBy: order.orderedBy,
      orderedFrom: order.orderedFrom,
      cost: this.calculateCost(order.bureau, order.reportType),
      rawData: order,
      notes: order.notes
    };

    this.reports.push(newReport);
    this.notifyListeners();

    // Simulate processing delay
    setTimeout(() => {
      this.updateReportStatus(newReport.id, 'processing');
    }, 1000);

    // Simulate completion after 3-7 seconds
    setTimeout(() => {
      this.completeReport(newReport.id);
    }, 3000 + Math.random() * 4000);

    return newReport;
  }

  private calculateCost(bureau: string, reportType: string): number {
    const costs = {
      'standard': { 'experian': 15, 'equifax': 15, 'transunion': 15, 'tri_merge': 35 },
      'monitoring': { 'experian': 25, 'equifax': 25, 'transunion': 25, 'tri_merge': 60 },
      'dispute_ready': { 'experian': 30, 'equifax': 30, 'transunion': 30, 'tri_merge': 75 },
      'tri_merge': { 'tri_merge': 45 }
    };
    return costs[reportType]?.[bureau] || 15;
  }

  private updateReportStatus(reportId: string, status: CreditReport['status']) {
    const report = this.reports.find(r => r.id === reportId);
    if (report) {
      report.status = status;
      this.notifyListeners();
    }
  }

  private completeReport(reportId: string) {
    const report = this.reports.find(r => r.id === reportId);
    if (report) {
      report.status = 'completed';
      report.completedDate = new Date().toISOString();
      report.pdfUrl = `/api/credit-reports/${reportId}/pdf`;

      // Add mock scores and summary
      if (report.bureau === 'tri_merge') {
        report.scores = {
          experian: 680 + Math.floor(Math.random() * 140),
          equifax: 675 + Math.floor(Math.random() * 145),
          transunion: 685 + Math.floor(Math.random() * 135)
        };
      } else {
        report.scores = {
          [report.bureau]: 650 + Math.floor(Math.random() * 170)
        };
      }

      report.summary = {
        totalAccounts: 8 + Math.floor(Math.random() * 15),
        openAccounts: 4 + Math.floor(Math.random() * 8),
        negativeItems: Math.floor(Math.random() * 6),
        inquiries: Math.floor(Math.random() * 8)
      };

      // Set expiration date (reports typically expire after 30-90 days)
      const expDate = new Date();
      expDate.setDate(expDate.getDate() + 30);
      report.expirationDate = expDate.toISOString();

      // Create CR record
      this.createCRRecord(report);

      this.notifyListeners();
    }
  }

  private generateCRNumber(): string {
    const timestamp = Date.now().toString().slice(-5);
    return `CR-${timestamp}`;
  }

  private createCRRecord(report: CreditReport) {
    const order = (report.rawData || {}) as Partial<CreditReportOrder>;
    const score = order.creditScore ?? (Object.values(report.scores || {})[0] || undefined);
    const makeAccounts = (count: number | undefined, prefix: string): any[] => {
      const safeCount = Math.max(0, Number(count ?? 0));
      return Array.from({ length: safeCount }).map((_, idx) => ({
        creditorName: `${prefix} ${idx + 1}`,
        accountNumber: `****${String(1000 + idx).slice(-4)}`,
        balance: 0,
        paymentStatus: 'Current',
        dateOpened: new Date().toISOString().slice(0, 10),
        lastActivity: new Date().toISOString().slice(0, 10),
      }));
    };

    const crRecord: CreditReportRecord = {
      id: this.generateCRNumber(),
      clientId: report.clientId,
      clientName: report.clientName,
      bureau: report.bureau,
      creditScore: score,
      alias: order.alias || `${report.clientName} - ${report.bureau}`,
      grade: order.grade || this.calculateGrade(score || 0),
      rating: order.rating || this.calculateRating(score || 0),
      description: order.description || `Credit report pulled from ${report.bureau} bureau`,
      pullDate: order.creditReportPullDate || report.completedDate || report.orderDate,
      productCode: order.productCode || `${report.bureau.slice(0,3)}1bReportScore`,
      reportKey: order.reportKey || `${report.id}_${Date.now()}`,
      currentEmployer: order.currentEmployer || '',
      previousEmployer: order.previousEmployer || '',
      overview: {
        minimumMonthlyPayment: order.minimumMonthlyPayment ?? 0,
        realEstate: order.realEstate ?? 0,
        installment: order.installment ?? 0,
        lineOfCredit: order.lineOfCredit ?? 0,
        totalDebts: order.totalDebts ?? 0,
        totalCreditors: order.totalCreditors ?? 0,
        goodStanding: order.goodStanding ?? 0,
        openAccounts: order.openAccounts ?? 0,
        closedAccounts: order.closedAccounts ?? 0,
        creditRepositoryIncluded:
          order.creditRepositoryIncluded && order.creditRepositoryIncluded.length > 0
            ? order.creditRepositoryIncluded
            : [report.bureau === 'tri_merge' ? 'Experian, Equifax, TransUnion' : report.bureau]
      },
      creditors: {
        auto: makeAccounts(order.autoCreditors, 'Auto Creditor'),
        mortgages: makeAccounts(order.mortgageCreditors, 'Mortgage Creditor'),
        creditCards: makeAccounts(order.creditCardCreditors, 'Card Creditor'),
        others: makeAccounts(order.otherCreditors, 'Other Creditor'),
      },
      systemInfo: {
        createdBy: report.orderedBy,
        status: order.creditReportStatus || 'success',
        createdDate: order.newFilesAttachedDateTime || new Date().toISOString(),
        totalFilesAttached: Number(order.totalFilesAttached ?? 0),
        attachedFiles: [report.pdfUrl || '']
      },
      pdfUrl: report.pdfUrl,
      relatedReports: [],
      approvalHistory: [
        {
          id: `approval_${Date.now()}`,
          approvedBy: report.orderedBy,
          approvalDate: new Date().toISOString(),
          action: 'Credit Report Completed',
          notes: 'Automatic approval upon report completion'
        }
      ]
    };

    this.crRecords.push(crRecord);
    this.notifyCRRecordListeners();
  }

  private calculateGrade(score: number): string {
    if (score >= 800) return 'A+';
    if (score >= 740) return 'A';
    if (score >= 670) return 'B';
    if (score >= 580) return 'C';
    if (score >= 300) return 'D';
    return 'F';
  }

  private calculateRating(score: number): string {
    if (score >= 800) return 'Excellent';
    if (score >= 740) return 'Very Good';
    if (score >= 670) return 'Good';
    if (score >= 580) return 'Fair';
    if (score >= 300) return 'Poor';
    return 'Very Poor';
  }

  // Get all reports for a client
  getClientReports(clientId: string): CreditReport[] {
    return this.reports.filter(r => r.clientId === clientId);
  }

  // Get all reports
  getAllReports(): CreditReport[] {
    return [...this.reports];
  }

  // Get report by ID
  getReport(reportId: string): CreditReport | undefined {
    return this.reports.find(r => r.id === reportId);
  }

  // Check if client has recent reports
  hasRecentReport(clientId: string, bureau?: string, daysOld: number = 30): boolean {
    const cutoff = new Date();
    cutoff.setDate(cutoff.getDate() - daysOld);

    return this.reports.some(r =>
      r.clientId === clientId &&
      r.status === 'completed' &&
      new Date(r.completedDate || r.orderDate) > cutoff &&
      (!bureau || r.bureau === bureau || r.bureau === 'tri_merge')
    );
  }

  // Get most recent report for client
  getMostRecentReport(clientId: string, bureau?: string): CreditReport | undefined {
    const clientReports = this.getClientReports(clientId)
      .filter(r => r.status === 'completed')
      .filter(r => !bureau || r.bureau === bureau || r.bureau === 'tri_merge')
      .sort((a, b) => new Date(b.completedDate || b.orderDate).getTime() - new Date(a.completedDate || a.orderDate).getTime());

    return clientReports[0];
  }

  // Delete a report
  deleteReport(reportId: string): boolean {
    const index = this.reports.findIndex(r => r.id === reportId);
    if (index !== -1) {
      this.reports.splice(index, 1);
      this.notifyListeners();
      return true;
    }
    return false;
  }

  // Add notes to a report
  addReportNote(reportId: string, note: string): boolean {
    const report = this.reports.find(r => r.id === reportId);
    if (report) {
      report.notes = report.notes ? `${report.notes}\n${note}` : note;
      this.notifyListeners();
      return true;
    }
    return false;
  }

  // CR Record Management Methods
  getAllCRRecords(): CreditReportRecord[] {
    return [...this.crRecords];
  }

  getClientCRRecords(clientId: string): CreditReportRecord[] {
    return this.crRecords.filter(r => r.clientId === clientId);
  }

  getCRRecord(crId: string): CreditReportRecord | undefined {
    return this.crRecords.find(r => r.id === crId);
  }

  updateCRRecord(crId: string, updates: Partial<CreditReportRecord>): boolean {
    const recordIndex = this.crRecords.findIndex(r => r.id === crId);
    if (recordIndex !== -1) {
      this.crRecords[recordIndex] = { ...this.crRecords[recordIndex], ...updates };
      this.notifyCRRecordListeners();
      return true;
    }
    return false;
  }

  deleteCRRecord(crId: string): boolean {
    const index = this.crRecords.findIndex(r => r.id === crId);
    if (index !== -1) {
      this.crRecords.splice(index, 1);
      this.notifyCRRecordListeners();
      return true;
    }
    return false;
  }

  private getMockReports(): CreditReport[] {
    return [
      {
        id: 'cr_existing_001',
        clientId: 'client_001',
        clientName: 'John Smith',
        bureau: 'tri_merge',
        reportType: 'standard',
        orderDate: '2025-09-15T10:30:00Z',
        completedDate: '2025-09-15T10:35:00Z',
        status: 'completed',
        orderedBy: 'Sarah Johnson',
        orderedFrom: 'credit_management',
        cost: 35,
        pdfUrl: '/api/credit-reports/cr_existing_001/pdf',
        scores: {
          experian: 720,
          equifax: 715,
          transunion: 725
        },
        summary: {
          totalAccounts: 12,
          openAccounts: 6,
          negativeItems: 2,
          inquiries: 3
        },
        expirationDate: '2025-10-15T10:35:00Z',
        notes: 'Initial credit assessment for client onboarding'
      },
      {
        id: 'cr_existing_002',
        clientId: 'client_002',
        clientName: 'Maria Garcia',
        bureau: 'experian',
        reportType: 'dispute_ready',
        orderDate: '2025-09-18T14:20:00Z',
        completedDate: '2025-09-18T14:25:00Z',
        status: 'completed',
        orderedBy: 'Lisa Brown',
        orderedFrom: 'credit_management',
        cost: 30,
        pdfUrl: '/api/credit-reports/cr_existing_002/pdf',
        scores: {
          experian: 650
        },
        summary: {
          totalAccounts: 8,
          openAccounts: 4,
          negativeItems: 5,
          inquiries: 6
        },
        expirationDate: '2025-10-18T14:25:00Z',
        notes: 'Pulled for dispute analysis - multiple negative items identified'
      }
    ];
  }
}

// Export singleton instance
export const creditReportService = CreditReportService.getInstance();